import { Loader, Grid, Card } from "components/lib";
import { applyFormTheme, useFormBrandColor } from "components/survey/formTheme";
import React, { useCallback, useRef, useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { StylesManager, Model } from "survey-core";
import { Survey } from "survey-react-ui";
import Axios from "axios";
import { get, set, del, keys } from "idb-keyval";
import { validatePoaUpload } from "./poa-upload-check";
import { uploadFailureMessage } from "./upload-error";
import { followSafeRedirect } from "./safe-redirect";
import "survey-core/defaultV2.min.css";
import { v4 as uuidv4 } from "uuid";

const Settings = require("settings.json");
const server_url = Settings[process.env.NODE_ENV].server_url;
const {
  validateABN,
  validateACN,
  validateTfn,
} = require("./australian-identifiers");

StylesManager.applyTheme("defaultV2");

// The label an entity is offered under on the Relationships page.
//
// This MUST match how the server derives client_group_entity.name
// (clientGroupOnboardingHandler's entity-type switch), because the submitted
// relationship rows are resolved back to entities by that name. Prefer the
// template's computed entity_display_name and fall back to the same fields the
// server reads, so a relationship is still selectable when the expression has
// not evaluated yet.
const entityDisplayName = (entity = {}) => {
  if (entity.entity_display_name) return String(entity.entity_display_name).trim();

  switch (entity.entity_type) {
    case "individual":
      return `${entity.firstName || entity.first_name || ""} ${
        entity.lastName || entity.last_name || ""
      }`.trim();
    case "trust":
    case "unit_trust":
    case "smsf":
      return (entity.trust_name || "").trim();
    case "body_corporate":
    case "not_for_profit":
      return (entity.company_name || "").trim();
    case "deceased_estate":
      return (entity.estate_name || "").trim();
    case "partnership":
      return (entity.partnership_name || "").trim();
    case "unincorporated_association":
      return (entity.association_name || "").trim();
    case "government_body":
      return (entity.gov_entity_name || "").trim();
    default:
      return "";
  }
};

const usePersistentResponseId = () => {
  const [id, setId] = useState(null);
  const params = useParams();

  useEffect(() => {
    (async () => {
      const storageKey = `clientOnboarding-${params.id}`;
      let stored = await get(storageKey);
      if (!stored) {
        stored = uuidv4();
        await set(storageKey, stored);
      }
      setId(stored);
    })();
  }, [params.id]);
  return id;
};

export function ClientOnboardingFormView() {
  const params = useParams();
  const id = params.id;
  const [formData, setFormData] = useState(null);
  // `undefined`, not null, is deliberate: useFormBrandColor reads a null theme as
  // "payload resolved, no theme" and would publish it over the frame colour View
  // seeded from the session cache, repainting the default gradient for the whole
  // fetch. The load path below always resolves this to a theme or null.
  const [formTheme, setFormTheme] = useState();

  // frames the page in the form's own colour (R8)
  useFormBrandColor(formTheme);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);

  const responseId = usePersistentResponseId();
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const isSubmittingRef = useRef(false);

  // Fetch form schema
  useEffect(() => {
    Axios.get(`/api/public/client-onboarding/${id}`)
      .then((res) => {
        setFormData(res.data?.data?.schema || null);
        setFormTheme(res.data?.data?.theme || null);
        setLoading(false);
      })
      .catch((err) => {
        setError(
          err.response?.status === 404
            ? "Onboarding form not found"
            : err.response?.status === 403
            ? "This onboarding form is not currently enabled"
            : "An error occurred loading the form"
        );
        setLoading(false);
      });
  }, [id]);

  // File upload handler
  const uploadFiles = useCallback(
    async (sender, options) => {
      const poaCheck = await validatePoaUpload({
        files: options.files,
        fieldName: options.name,
        formSchema: formData,
        onboardingId: id,
        serverUrl: server_url,
      });

      if (!poaCheck.valid) {
        options.callback("error", poaCheck.message);
        return;
      }

      const fd = new FormData();
      options.files.forEach((file) => {
        fd.append("file", file);
        fd.append("response_id", responseId);
        fd.append("field_name", options.name);
        fd.append("file_name", file.name);
      });
      Axios.post(server_url + "/api/utility/gcp/upload", fd).then((data) => {
        options.callback(
          "success",
          options.files.map((file) => ({
            file: file,
            name: data.data.name,
            type: data.data.name,
            content: data.data.url,
            id: file.id,
          }))
        );
      }).catch((err) => {
        options.callback("error", uploadFailureMessage(err));
      });
    },
    [formData, id, responseId]
  );

  // Form submission handler
  const onComplete = useCallback(
    (sender) => {
      if (isSubmittingRef.current) return;
      isSubmittingRef.current = true;

      const resultData = [];
      for (const key in sender.data) {
        const question = sender.getQuestionByName(key);
        if (question) {
          const item = { name: key };
          if (question.getType() === "file") {
            item.value = [];
            (question.value || []).forEach((fileItem) => {
              item.value.push({
                ...fileItem,
                key: `${responseId}/${key}/${fileItem.name}`,
              });
            });
          } else {
            item.value = question.value;
          }
          resultData.push(item);
        }
      }

      const outputObject = {};
      resultData.forEach((item) => {
        outputObject[item.name] = item.value;
      });

      Axios.post(`/api/public/client-onboarding/${id}`, outputObject)
        .then(async (response) => {
          // Clear cached draft data
          const allKeys = await keys();
          for (const k of allKeys) {
            if (
              k.includes(`clientOnboarding-${id}`) ||
              k.includes(`formDraft-client-${id}`)
            ) {
              await del(k);
            }
          }

          setSubmissionResult(response.data);
          setSubmitted(true);

          // Only an absolute http(s) redirect is followed (stored XSS guard).
          followSafeRedirect(response.data.redirect);
        })
        .catch((err) => {
          console.error("Submission error:", err);
          setError("Failed to submit the form. Please try again.");
        })
        .finally(() => {
          isSubmittingRef.current = false;
        });
    },
    [id, responseId]
  );

  // ABN validation
  function isValidABN(sender, options) {
    const question = options.question;
    if (
      question.name.toLowerCase().includes("abn") &&
      options.value
    ) {
      const error = validateABN(options.value);
      if (error) return (options.error = error);
    }
  }

  function isValidACN(sender, options) {
    const question = options.question;
    if (
      question.name.toLowerCase().includes("acn") &&
      options.value
    ) {
      const error = validateACN(options.value);
      if (error) return (options.error = error);
    }
  }

  function isValidTFN(sender, options) {
    const question = options.question;
    const isTfnQuestion =
      question.jsonObj?.tfn === true || /(^|_)tfn$/i.test(question.name || "");
    if (!isTfnQuestion || !options.value) return;

    const error = validateTfn(options.value);
    if (error) return (options.error = error);
  }

  function validateQuestion(sender, options) {
    isValidABN(sender, options);
    if (!options.error) isValidACN(sender, options);
    if (!options.error) isValidTFN(sender, options);
  }

  // Create model.
  //
  // The relationship endpoint dropdowns are populated imperatively (see
  // syncEntityChoices below) rather than through SurveyJS's choicesFromQuestion.
  // choicesFromQuestion cannot read a paneldynamic, and routing it through a
  // hidden proxy question silently LOST the answers: at completion SurveyJS
  // drops any value that is not in its question's own choice list, so
  // from_entity/to_entity were stripped from every submission and only
  // relationship_role survived. Strip the linkage from the schema so nothing
  // re-derives an empty choice list at completion.
  const model = useMemo(() => {
    if (!formData) return null;
    const schema = JSON.parse(JSON.stringify(formData));

    const relPage = schema.pages?.find((p) => p.name === "relationships");
    const relPanelDef = relPage?.elements?.find(
      (e) => e.name === "entity_relationships"
    );
    relPanelDef?.templateElements?.forEach((el) => {
      if (el.name === "from_entity" || el.name === "to_entity") {
        delete el.choicesFromQuestion;
        delete el.choicesFromQuestionMode;
        delete el.choiceValuesFromQuestion;
        delete el.choiceTextsFromQuestion;
        el.choices = [];
      }
    });

    return applyFormTheme(new Model(schema), formTheme);
  }, [formData, formTheme]);

  // Setup model effects
  useEffect(() => {
    if (!model || !responseId) return;

    // Restore draft, then sync entity choices
    const restoreDraft = async () => {
      const draftKey = `formDraft-client-${id}`;
      const draft = await get(draftKey);
      if (draft) {
        model.data = draft;
      }
      syncEntityChoices();
    };
    restoreDraft();

    // Put the current entity names on the relationship dropdowns.
    //
    // The choices have to land on the template question AND on every panel that
    // already exists — a panel instantiated before the entity was named keeps
    // its own choice list, and a value missing from that list is discarded when
    // the survey completes.
    const syncEntityChoices = () => {
      const relQuestion = model.getQuestionByName("entity_relationships");
      if (!relQuestion) return;

      const choices = (model.getValue("entities") || [])
        .map(entityDisplayName)
        .filter(Boolean)
        .map((name) => ({ value: name, text: name }));

      const apply = (container) => {
        if (!container) return;
        ["from_entity", "to_entity"].forEach((name) => {
          const question = container.getQuestionByName?.(name);
          if (question) question.choices = choices;
        });
      };

      apply(relQuestion.template);
      (relQuestion.panels || []).forEach(apply);
    };

    const onDynPanelAdded = () => syncEntityChoices();
    model.onDynamicPanelAdded.add(onDynPanelAdded);

    // Sync once after draft restore, and on every value change
    const saveDraft = () => {
      const draftKey = `formDraft-client-${id}`;
      set(draftKey, model.data);
      syncEntityChoices();
    };
    model.onValueChanged.add(saveDraft);
    model.onUploadFiles.add(uploadFiles);
    model.onComplete.add(onComplete);
    model.onValidateQuestion.add(validateQuestion);

    return () => {
      model.onValueChanged.remove(saveDraft);
      model.onUploadFiles.remove(uploadFiles);
      model.onComplete.remove(onComplete);
      model.onValidateQuestion.remove(validateQuestion);
      model.onDynamicPanelAdded.remove(onDynPanelAdded);
    };
  }, [model, responseId, id, onComplete, uploadFiles]);

  // Upload progress overlay
  const Overlay = () => (
    <div className="fixed inset-0 grid place-items-center bg-black/50 backdrop-blur-sm z-50">
      <div className="w-80 rounded-2xl bg-white shadow-2xl p-6 flex flex-col items-center gap-4">
        <h2 className="text-lg font-semibold text-gray-800">
          Uploading your files...
        </h2>
        <progress
          value={pct}
          max={100}
          className="w-full h-3 rounded-full overflow-hidden bg-gray-200"
        />
        <span className="text-sm text-gray-600">{pct}%</span>
      </div>
    </div>
  );

  if (loading || !responseId) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="min-w-min w-3/4 mx-auto mt-12">
        <Grid>
          <Card>
            <div className="text-center py-12">
              <h2 className="text-xl font-semibold text-gray-700 mb-2">
                {error}
              </h2>
              <p className="text-gray-500">
                Please check the URL and try again, or contact the firm that
                sent you this link.
              </p>
            </div>
          </Card>
        </Grid>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-w-min w-3/4 mx-auto mt-12">
        <Grid>
          <Card>
            <div className="text-center py-12">
              <div className="text-5xl mb-4">&#10003;</div>
              <h2 className="text-xl font-semibold text-gray-700 mb-2">
                Onboarding Form Submitted
              </h2>
              <p className="text-gray-500">
                Thank you for completing the onboarding form. Your information
                has been received and is being processed.
              </p>
              {submissionResult?.message && (
                <p className="text-gray-400 mt-4 text-sm">
                  {submissionResult.message}
                </p>
              )}
            </div>
          </Card>
        </Grid>
      </div>
    );
  }

  if (!model) {
    return <Loader />;
  }

  return (
    <>
      <div className="min-w-min w-3/4">
        <Grid>
          <Card>
            <Survey model={model} />
          </Card>
        </Grid>
      </div>
      {busy && <Overlay />}
    </>
  );
}

export default ClientOnboardingFormView;
